import Head from 'next/head';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import config from '@/config';
import i18n from '@/i18n.json';
import { localizePath } from '@/utils/locale-path';

/**
 * Page-specific meta tags, canonical URL and hreflang alternates.
 * @param {Object} props
 * @param {string} [props.url] - Canonical URL of this page in the current locale.
 * @param {string} [props.hreflangPath] - Path shared by every locale (`/networks/solana`, `/` for the home page). Defaults to the current path. Alternates never end in a slash, except the default locale's home page (the bare origin plus `/`), matching sitemap.xml.
 * @param {Array<{locale: string, href: string}>|Record<string, string>} [props.hreflangUrls] - Exact URL per locale, for pages whose path differs between locales.
 * @param {boolean} [props.noindex] - Keep the page out of search results while still letting crawlers follow its links.
 * @param {string} [props.imageUrl] - Absolute URL of the page's share image, from `shareImage` in `@/utils/share-image`. Without it the page keeps the default image set by DefaultHead (which also sets its size and type: every share image is 1200x630).
 * @param {string} [props.imageAlt] - What the image shows. Defaults to the title.
 */
const SEO = ({
  title,
  description,
  imageUrl,
  imageAlt,
  url,
  hreflangPath,
  hreflangUrls,
  noindex,
}) => {
  const { asPath } = useRouter();

  const { hreflangLinks, xDefaultHref } = useMemo(() => {
    if (
      hreflangUrls &&
      (Array.isArray(hreflangUrls) ? hreflangUrls.length > 0 : Object.keys(hreflangUrls).length > 0)
    ) {
      const byLocale = Array.isArray(hreflangUrls)
        ? Object.fromEntries(hreflangUrls.map(({ locale, href }) => [locale, href]))
        : hreflangUrls;
      const links = i18n.locales
        .map((loc) => {
          const href = byLocale[loc];
          if (!href) return null;
          return <link key={`hreflang-${loc}`} rel="alternate" hrefLang={loc} href={href} />;
        })
        .filter(Boolean);
      const xDefaultHrefValue = byLocale[i18n.defaultLocale] || byLocale[i18n.locales[0]];
      return { hreflangLinks: links, xDefaultHref: xDefaultHrefValue };
    }

    // `/` is `/` for the default locale and `/fr` (not `/fr/`, which
    // redirects) for the others.
    const path = hreflangPath || asPath.split('?')[0];
    const hrefFor = (loc) => `${config.rootUrl}${localizePath(path, loc)}`;
    const links = i18n.locales.map((loc) => (
      <link key={`hreflang-${loc}`} rel="alternate" hrefLang={loc} href={hrefFor(loc)} />
    ));
    return { hreflangLinks: links, xDefaultHref: hrefFor(i18n.defaultLocale) };
  }, [hreflangPath, hreflangUrls, asPath]);

  return (
    <Head>
      {title && (
        <>
          <title key="title">{`${title} - Bidali`}</title>
          <meta name="twitter:title" content={title} key="twittertitle" />
          <meta property="og:title" content={title} key="ogtitle" />
          <meta itemProp="about" content={title} key="itempropabout" />
        </>
      )}

      {description && (
        <>
          <meta name="description" content={description} key="description" />
          <meta name="twitter:description" content={description} key="twitterdescription" />
          <meta property="og:description" content={description} key="ogdescription" />
          <meta itemProp="description" content={description} key="itempropdescription" />
        </>
      )}

      {imageUrl && (
        <>
          <meta name="twitter:image" content={imageUrl} key="twitterimage" />
          {/* The name X read before `twitter:image`; some scrapers still look for it. */}
          <meta name="twitter:image:src" content={imageUrl} key="twitterimagesrc" />
          <meta property="og:image" content={imageUrl} key="ogimage" />
          {/* One element each, not a nested fragment: next/head only de-duplicates keys one level deep. */}
          {(imageAlt || title) && (
            <meta property="og:image:alt" content={imageAlt || title} key="ogimagealt" />
          )}
          {(imageAlt || title) && (
            <meta name="twitter:image:alt" content={imageAlt || title} key="twitterimagealt" />
          )}
          <meta itemProp="image" content={imageUrl} key="itempropimage" />
        </>
      )}

      {url && (
        <>
          <link rel="canonical" href={url} key="canonical" />
          <meta property="og:url" content={url} key="ogurl" />
        </>
      )}

      {noindex && <meta name="robots" content="noindex, follow" key="robots" />}

      {hreflangLinks}
      <link key="hreflang-x-default" rel="alternate" hrefLang="x-default" href={xDefaultHref} />
    </Head>
  );
};

export default SEO;
